/**
 * SOP Detail Page
 * 
 * Comprehensive view of a single Standard Operating Procedure with:
 * - Full procedure details (Purpose, Steps, Inputs, Decision Points)
 * - Cross-links to related Pipeline Stage, Roles, and SLAs
 * - EN/AR bilingual support with RTL
 * - Escalation & exception handling details
 * - Audit/logging requirements
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  ChevronRight,
  Layers,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  Target,
  AlertCircle,
  Book,
  Zap,
  ListChecks,
  UserCheck,
  Calendar,
  ArrowLeft,
  ExternalLink,
  ClipboardList,
  GitBranch,
  FileSearch,
  Check,
  X,
  Info
} from 'lucide-react';
import { getSOPById, getSOPsForStage } from '../../lib/sopsRegistry';
import { getStageById } from '../../lib/leadPipelineRegistry';
import { getRoleById, getRoleGroups } from '../../lib/rolesRegistry';
import { getChannelSLAById, getStageSLAById } from '../../lib/slaCatalogueRegistry';
import type { SOPPriority, SOPType } from '../../types';

// =============================================================================
// Helper Components
// =============================================================================

// Priority Badge
const PriorityBadge: React.FC<{ priority: SOPPriority; isArabic: boolean }> = ({ priority, isArabic }) => {
  const config = {
    critical: { 
      bg: 'bg-red-100 dark:bg-red-900/40', 
      text: 'text-red-700 dark:text-red-300',
      border: 'border-red-200 dark:border-red-800',
      label: isArabic ? 'حرج' : 'Critical',
      icon: <AlertCircle className="w-4 h-4" />
    },
    high: { 
      bg: 'bg-orange-100 dark:bg-orange-900/40', 
      text: 'text-orange-700 dark:text-orange-300',
      border: 'border-orange-200 dark:border-orange-800',
      label: isArabic ? 'عالي' : 'High',
      icon: <AlertTriangle className="w-4 h-4" />
    },
    medium: { 
      bg: 'bg-yellow-100 dark:bg-yellow-900/40', 
      text: 'text-yellow-700 dark:text-yellow-300',
      border: 'border-yellow-200 dark:border-yellow-800',
      label: isArabic ? 'متوسط' : 'Medium',
      icon: <Target className="w-4 h-4" />
    },
    low: { 
      bg: 'bg-green-100 dark:bg-green-900/40', 
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-200 dark:border-green-800',
      label: isArabic ? 'منخفض' : 'Low',
      icon: <CheckCircle className="w-4 h-4" />
    },
  };
  
  const c = config[priority];
  
  return (
    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-medium ${c.bg} ${c.text} ${c.border}`}>
      {c.icon}
      {c.label}
    </span>
  );
};

// SOP Type Badge
const SOPTypeBadge: React.FC<{ type: SOPType; isArabic: boolean }> = ({ type, isArabic }) => {
  const config = {
    stage: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-700 dark:text-blue-300',
      icon: <Layers className="w-4 h-4" />,
      label: isArabic ? 'إجراء مرحلة' : 'Stage SOP'
    },
    scenario: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-700 dark:text-purple-300',
      icon: <AlertTriangle className="w-4 h-4" />,
      label: isArabic ? 'إجراء سيناريو' : 'Scenario SOP'
    }
  };
  
  const c = config[type];
  
  return (
    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {c.label}
    </span>
  );
};

// Section Card Component
const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  color?: string;
}> = ({ title, icon, children, color = 'gray' }) => {
  const borderColors: Record<string, string> = {
    gray: 'border-gray-200 dark:border-gray-700',
    blue: 'border-blue-200 dark:border-blue-800',
    green: 'border-green-200 dark:border-green-800',
    amber: 'border-amber-200 dark:border-amber-800',
    red: 'border-red-200 dark:border-red-800',
    purple: 'border-purple-200 dark:border-purple-800',
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border ${borderColors[color]} overflow-hidden`}>
      <div className={`px-6 py-4 border-b ${borderColors[color]} bg-gray-50 dark:bg-gray-900`}>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          {icon}
          {title}
        </h3>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const SOPDetail: React.FC = () => {
  const { sopId } = useParams<{ sopId: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  
  // Get SOP data
  const sop = sopId ? getSOPById(sopId) : null;
  
  // Get related data
  const stage = sop?.meta.stageId ? getStageById(sop.meta.stageId) : null;
  const primaryRole = sop?.meta.primaryOwnerRoleId ? getRoleById(sop.meta.primaryOwnerRoleId) : null;
  const supportingRoles = sop?.meta.supportingRoleIds.map(id => getRoleById(id)).filter(Boolean) || [];
  const roleGroups = getRoleGroups();
  // allStages removed - using getStageById directly where needed
  
  // Get related SLAs
  const relatedSLAs = sop?.meta.relatedSlaIds.map(slaId => {
    if (slaId.startsWith('channel_')) {
      const sla = getChannelSLAById(slaId);
      return sla ? { type: 'channel' as const, sla } : null;
    } else if (slaId.startsWith('stage_sla_')) {
      const sla = getStageSLAById(slaId);
      return sla ? { type: 'stage' as const, sla } : null;
    }
    return null;
  }).filter(Boolean) || [];

  // Get other SOPs for the same stage
  const relatedSOPs = sop?.meta.stageId ? getSOPsForStage(sop.meta.stageId).filter(s => s.meta.id !== sop.meta.id) : [];

  // 404 handling
  if (!sop) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
          <FileText className="w-10 h-10 text-gray-400" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {isArabic ? 'الإجراء غير موجود' : 'SOP Not Found'}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {isArabic 
            ? `لم يتم العثور على إجراء بالمعرف: ${sopId}` 
            : `No SOP found with ID: ${sopId}`
          }
        </p>
        <Link 
          to="/sops" 
          className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors"
        >
          <ArrowLeft className={`w-5 h-5 ${isArabic ? 'rotate-180' : ''}`} />
          {isArabic ? 'العودة إلى الإجراءات' : 'Back to SOPs'}
        </Link>
      </div>
    );
  }

  const primaryRoleGroup = primaryRole ? roleGroups.find(g => g.id === primaryRole.group) : null;

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/sops" className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors">
          {isArabic ? 'الإجراءات' : 'SOPs'}
        </Link>
        <ChevronRight className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
        <span className="text-gray-900 dark:text-white font-medium truncate">
          {isArabic ? sop.meta.titleAr : sop.meta.title}
        </span>
      </nav>

      {/* Hero Header */}
      <div className="bg-gradient-to-br from-amber-600 via-orange-600 to-red-600 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
        <div className="relative z-10">
          {/* Back Button */}
          <button
            onClick={() => navigate('/sops')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className={`w-5 h-5 ${isArabic ? 'rotate-180' : ''}`} />
            {isArabic ? 'العودة إلى الإجراءات' : 'Back to SOPs'}
          </button>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <SOPTypeBadge type={sop.meta.type} isArabic={isArabic} />
                <PriorityBadge priority={sop.meta.priority} isArabic={isArabic} />
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-bold mb-3">
                {isArabic ? sop.meta.titleAr : sop.meta.title}
              </h1>
              <p className="text-white/80 text-lg mb-4">
                {isArabic ? sop.meta.shortDescAr : sop.meta.shortDesc}
              </p>

              {/* Brands & Date */}
              <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {isArabic ? 'آخر تحديث: ' : 'Last Updated: '}{sop.meta.lastUpdated}
                </span>
                <span className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  {sop.meta.appliesToBrands.join(', ')}
                </span>
              </div>
            </div>

            {/* Stage Link */}
            {stage && (
              <Link
                to={`/pipeline/stage/${stage.meta.id}`}
                className="flex-shrink-0 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                    style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                  >
                    {stage.meta.order}
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wide">
                      {isArabic ? 'المرحلة المرتبطة' : 'Related Stage'}
                    </p>
                    <p className="text-white font-semibold flex items-center gap-2">
                      {isArabic ? stage.meta.titleAr : stage.meta.title}
                      <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Purpose */}
          <SectionCard 
            title={isArabic ? 'الغرض' : 'Purpose'} 
            icon={<Book className="w-5 h-5 text-blue-500" />}
            color="blue"
          >
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
              {isArabic ? sop.purposeAr || sop.purpose : sop.purpose}
            </p>
          </SectionCard>

          {/* When to Use / Triggers */}
          <SectionCard 
            title={isArabic ? 'متى يُستخدم / المحفزات' : 'When to Use / Triggers'} 
            icon={<Zap className="w-5 h-5 text-amber-500" />}
            color="amber"
          >
            {sop.meta.triggers.length > 0 ? (
              <ul className="space-y-2">
                {sop.meta.triggers.map((trigger, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xs font-bold mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300">{trigger}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">
                {isArabic ? 'لا توجد محفزات محددة' : 'No specific triggers defined'}
              </p>
            )}
          </SectionCard>

          {/* Inputs */}
          {sop.inputs.length > 0 && (
            <SectionCard 
              title={isArabic ? 'المدخلات المطلوبة' : 'Required Inputs'} 
              icon={<ClipboardList className="w-5 h-5 text-purple-500" />}
              color="purple"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        {isArabic ? 'الحقل' : 'Field'}
                      </th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        {isArabic ? 'مطلوب' : 'Required'}
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        {isArabic ? 'المصدر' : 'Source'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sop.inputs.map((input, idx) => (
                      <tr 
                        key={idx}
                        className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900/50' : ''}
                      >
                        <td className="py-3 px-4 text-gray-900 dark:text-white font-medium">
                          {input.field}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {input.required === true || input.required === 'Yes' ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : input.required === false || input.required === 'No' ? (
                            <X className="w-5 h-5 text-gray-400 mx-auto" />
                          ) : (
                            <span className="text-amber-600 dark:text-amber-400 text-xs">{input.required}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                          {input.source}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* Steps */}
          {sop.steps.length > 0 && (
            <SectionCard 
              title={isArabic ? 'الخطوات' : 'Steps'} 
              icon={<ListChecks className="w-5 h-5 text-green-500" />}
              color="green"
            >
              <div className="space-y-6">
                {sop.steps.map((action, actionIdx) => (
                  <div key={actionIdx} className="relative">
                    {/* Action Header */}
                    <div className="flex items-start gap-4 mb-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 font-bold">
                        {actionIdx + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                          {action.action}
                        </h4>
                      </div>
                    </div>
                    
                    {/* Steps */}
                    {action.steps.length > 0 && (
                      <div className="ml-12 space-y-2">
                        {action.steps.map((step, stepIdx) => (
                          <div 
                            key={stepIdx}
                            className="flex items-start gap-3 py-2 px-4 rounded-lg bg-gray-50 dark:bg-gray-900/50"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-medium">
                              {actionIdx + 1}.{stepIdx + 1}
                            </span>
                            <span className="text-gray-700 dark:text-gray-300">
                              {step.step}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Decision Points */}
          {sop.decisionPoints.length > 0 && (
            <SectionCard 
              title={isArabic ? 'نقاط القرار' : 'Decision Points'} 
              icon={<GitBranch className="w-5 h-5 text-indigo-500" />}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        {isArabic ? 'الشرط' : 'Condition'}
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        {isArabic ? 'النتيجة' : 'Outcome'}
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        {isArabic ? 'الإجراء التالي' : 'Next Action'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sop.decisionPoints.map((dp, idx) => (
                      <tr 
                        key={idx}
                        className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900/50' : ''}
                      >
                        <td className="py-3 px-4 text-gray-900 dark:text-white">
                          {dp.condition}
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                          {dp.outcome}
                        </td>
                        <td className="py-3 px-4 text-indigo-600 dark:text-indigo-400 font-medium">
                          {dp.nextAction}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* Definition of Done */}
          {sop.meta.definitionOfDone.length > 0 && (
            <SectionCard 
              title={isArabic ? 'تعريف الإنجاز' : 'Definition of Done'} 
              icon={<CheckCircle className="w-5 h-5 text-green-500" />}
              color="green"
            >
              <ul className="space-y-2">
                {sop.meta.definitionOfDone.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* SLA Checks */}
          {sop.slaChecks.length > 0 && (
            <SectionCard 
              title={isArabic ? 'فحوصات اتفاقية مستوى الخدمة' : 'SLA Checks'} 
              icon={<Clock className="w-5 h-5 text-blue-500" />}
              color="blue"
            >
              <ul className="space-y-2">
                {sop.slaChecks.map((check, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{check}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* Escalation & Exceptions */}
          {sop.escalationExceptions.length > 0 && (
            <SectionCard 
              title={isArabic ? 'التصعيد والاستثناءات' : 'Escalation & Exceptions'} 
              icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
              color="red"
            >
              <ul className="space-y-2">
                {sop.escalationExceptions.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* Audit/Logging Requirements */}
          {sop.auditRequirements.length > 0 && (
            <SectionCard 
              title={isArabic ? 'متطلبات التدقيق والتسجيل' : 'Audit/Logging Requirements'} 
              icon={<FileSearch className="w-5 h-5 text-gray-500" />}
            >
              <ul className="space-y-2">
                {sop.auditRequirements.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <FileSearch className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Primary Owner */}
          {primaryRole && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-500" />
                {isArabic ? 'المسؤول الرئيسي' : 'Primary Owner'}
              </h3>
              <Link
                to={`/roles/${primaryRole.id}`}
                className="block p-4 rounded-xl bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${primaryRoleGroup?.color || '#10b981'}20` }}
                  >
                    <UserCheck 
                      className="w-6 h-6"
                      style={{ color: primaryRoleGroup?.color || '#10b981' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white group-hover:text-emerald-600 transition-colors">
                      {isArabic ? primaryRole.titleAr : primaryRole.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isArabic ? primaryRoleGroup?.labelAr : primaryRoleGroup?.label}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 transition-colors" />
                </div>
              </Link>
            </div>
          )}

          {/* Supporting Roles */}
          {supportingRoles.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                {isArabic ? 'الأدوار الداعمة' : 'Supporting Roles'}
              </h3>
              <div className="space-y-2">
                {supportingRoles.map(role => {
                  if (!role) return null;
                  const roleGroup = roleGroups.find(g => g.id === role.group);
                  return (
                    <Link
                      key={role.id}
                      to={`/roles/${role.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                    >
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${roleGroup?.color || '#3b82f6'}20` }}
                      >
                        <Users 
                          className="w-4 h-4"
                          style={{ color: roleGroup?.color || '#3b82f6' }}
                        />
                      </div>
                      <span className="flex-1 text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {isArabic ? role.titleAr : role.title}
                      </span>
                      <ChevronRight className={`w-4 h-4 text-gray-400 ${isArabic ? 'rotate-180' : ''}`} />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Related SLAs */}
          {relatedSLAs.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-500" />
                {isArabic ? 'اتفاقيات مستوى الخدمة المرتبطة' : 'Related SLAs'}
              </h3>
              <div className="space-y-2">
                {relatedSLAs.map((item, idx) => {
                  if (!item) return null;
                  const { type, sla } = item;
                  const path = type === 'channel' 
                    ? `/sla-catalogue/channel/${sla.id}`
                    : `/sla-catalogue/stage/${sla.id}`;
                  const label = type === 'channel'
                    ? (isArabic ? (sla as any).channelAr : (sla as any).channel)
                    : (isArabic ? (sla as any).stageNameAr : (sla as any).stageName);
                  
                  return (
                    <Link
                      key={idx}
                      to={path}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        type === 'channel' 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-purple-100 dark:bg-purple-900/30'
                      }`}>
                        <Clock className={`w-4 h-4 ${
                          type === 'channel' ? 'text-green-600' : 'text-purple-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors truncate">
                          {label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {type === 'channel' ? (isArabic ? 'قناة' : 'Channel') : (isArabic ? 'مرحلة' : 'Stage')}
                        </p>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-gray-400 ${isArabic ? 'rotate-180' : ''}`} />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Related SOPs for Same Stage */}
          {relatedSOPs.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                {isArabic ? 'إجراءات أخرى لنفس المرحلة' : 'Other SOPs for This Stage'}
              </h3>
              <div className="space-y-2">
                {relatedSOPs.slice(0, 5).map(relatedSOP => (
                  <Link
                    key={relatedSOP.meta.id}
                    to={`/sops/${relatedSOP.meta.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-100 dark:bg-amber-900/30">
                      <FileText className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors truncate">
                      {isArabic ? relatedSOP.meta.titleAr : relatedSOP.meta.title}
                    </span>
                    <ChevronRight className={`w-4 h-4 text-gray-400 ${isArabic ? 'rotate-180' : ''}`} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Quick Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" />
              {isArabic ? 'معلومات سريعة' : 'Quick Info'}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">{isArabic ? 'المعرف' : 'ID'}:</span>
                <span className="font-mono text-blue-900 dark:text-blue-100">{sop.meta.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">{isArabic ? 'النوع' : 'Type'}:</span>
                <span className="text-blue-900 dark:text-blue-100 capitalize">{sop.meta.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">{isArabic ? 'الأولوية' : 'Priority'}:</span>
                <span className="text-blue-900 dark:text-blue-100 capitalize">{sop.meta.priority}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">{isArabic ? 'عدد الخطوات' : 'Steps'}:</span>
                <span className="text-blue-900 dark:text-blue-100">{sop.steps.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">{isArabic ? 'المدخلات' : 'Inputs'}:</span>
                <span className="text-blue-900 dark:text-blue-100">{sop.inputs.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOPDetail;
